/* ── scrollPadding ──────────────────────────────────────────────
 * Keeps keyboard focus from landing underneath a bottom-sticky bar.
 *
 * When focus moves (Tab, or a field focused from code) the browser
 * scrolls the focused control into view — but it only aims for the
 * scroll container's visible box, which still includes the strip the
 * sticky save bar is covering. So on a long form the next field is
 * "scrolled into view" straight under the bar, and the user tabs
 * blind. `scroll-padding-bottom` on the scroll container is the
 * standard fix: it tells the browser the bottom N px are not really
 * visible, and every focus / scrollIntoView / find-in-page scroll
 * respects it.
 *
 * Which scroll container: the viewport is scrolled by the ROOT element
 * (<html>), so that is where the padding goes. A bar inside an inner
 * scroller (a drawer body, an `overflow: auto` panel) sticks to that
 * scroller instead, so it gets the padding as well.
 *
 * Why a registry rather than "save old value, restore on unmount":
 * two bars can be visible at once (a page bar plus one in a drawer),
 * and they can hide in any order. Each bar claims a height per
 * element; the element gets the tallest claim; the value that was
 * there before the FIRST claim is put back only when the LAST claim
 * on that element is released.
 * ────────────────────────────────────────────────────────────── */

// Breathing room between a focused control and the top of the bar.
export const FOCUS_GAP = 16;

// element -> { saved: string, claims: Map<token, height> }
const registry = new Map();

const apply = (element, entry) => {
  const tallest = Math.max(0, ...entry.claims.values());
  element.style.scrollPaddingBottom = `${Math.ceil(tallest) + FOCUS_GAP}px`;
};

/**
 * Register (or update) `token`'s bar height on `element`. Call again
 * with a new height when the bar resizes (e.g. it wraps to two rows on
 * a phone). Returns nothing; pair every claim with releaseScrollPadding.
 */
export const claimScrollPadding = (element, token, height) => {
  if (!element) return;
  let entry = registry.get(element);
  if (!entry) {
    // Inline value only — a value from a stylesheet is untouched by an
    // inline override and comes back by itself when we clear ours.
    entry = { saved: element.style.scrollPaddingBottom || '', claims: new Map() };
    registry.set(element, entry);
  }
  entry.claims.set(token, height);
  apply(element, entry);
};

export const releaseScrollPadding = (element, token) => {
  const entry = element && registry.get(element);
  if (!entry || !entry.claims.delete(token)) return;
  if (entry.claims.size > 0) {
    apply(element, entry);
    return;
  }
  element.style.scrollPaddingBottom = entry.saved;
  registry.delete(element);
};

/**
 * The element whose scroll-padding matters for a bar: its nearest
 * vertically scrollable ancestor, or null when that is the viewport
 * (the caller always pads the root element for that case).
 */
export const findInnerScrollContainer = (node) => {
  if (typeof window === 'undefined' || !node) return null;
  let current = node.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};
